import type { AsyncDataController, AsyncDataProps, AsyncDataState } from '@snapchat/async-data';
import { asyncDataController } from '@snapchat/async-data';
import { createContext, useCallback, useContext, useState } from 'react';

interface AsyncDataContextProps {
  controller: AsyncDataController;
}

export const AsyncDataContext = createContext<AsyncDataContextProps>({
  controller: asyncDataController,
});

export interface UseAsyncDataProps<T> extends AsyncDataProps<T> {
  /** Turns off calling the async data and always returns 'undefined'. */
  skip?: boolean;
}

/**
 * Helper hook to interface with the asyncData controller.
 *
 * Can exclude from test coverage, since this is a very simple hook that interfaces with
 * asyncDataController. Low ROI on testing this since it's really only testing one line.
 *
 * @returns {data, error, isLoading, hasLoaded} From registering the request to the async data
 *   controller
 */
export function useAsyncData<T>(
  props: UseAsyncDataProps<T>
): Pick<AsyncDataState<T>, 'data' | 'error' | 'isLoading' | 'hasLoaded'> {
  const { controller } = useContext(AsyncDataContext);

  const [_invariant, setInvariant] = useState<number>(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We do want the invariant to trigger change.
  const onData = useCallback(
    (data: T) => {
      props.onData?.(data);
      setInvariant(invariant => invariant + 1);
    },
    // Skipping deps on ALL props.
    [setInvariant, props.onData]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: We do want the invariant to trigger change.
  const onError = useCallback(
    (error: unknown) => {
      props.onError?.(error);
      setInvariant(invariant => invariant + 1);
    },
    // Skipping deps on ALL props.
    [setInvariant, props.onError]
  );

  // Do nothing if there's an explicit skip.
  if (props.skip) {
    return {
      hasLoaded: true,
      isLoading: false,
    };
  }

  return controller.register({
    ...props,
    onError,
    onData,
  });
}
